import { useGetMonthName } from 'helpers/hooks'

import styles from './StreamsArchiveCard.module.scss'

interface IStreamInfo {
  startedAt: string | null
  image: string
  durationMs?: number | null
  profit?: number | null
  videoUrl?: string | null
}

export const StreamsArchiveCard = ({
  streamInfo,
  onClick,
  selected = false,
  onSelect
}: {
  streamInfo: IStreamInfo
  onClick?: () => void
  selected?: boolean
  onSelect?: () => void
}) => {
  const monthName = useGetMonthName()

  const startedDate = streamInfo.startedAt
    ? new Date(streamInfo.startedAt)
    : null

  const formatDuration = (durationMs?: number | null) => {
    if (!durationMs || durationMs <= 0) return '00:00'

    const totalSeconds = Math.floor(durationMs / 1000)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    if (hours > 0) {
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
        2,
        '0'
      )}:${String(seconds).padStart(2, '0')}`
    }

    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
      2,
      '0'
    )}`
  }

  return (
    <div
      className={styles.container}
      onClick={onClick}
      role='button'
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick?.()
        }
      }}
      style={{
        backgroundImage: `url("${streamInfo.image}")`,
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        cursor: 'pointer'
      }}
    >
      <button
        type='button'
        className={`${styles.circle} ${
          selected ? styles.circle_selected : ''
        }`}
        onClick={(e) => {
          e.stopPropagation()
          onSelect?.()
        }}
        aria-label={selected ? 'Deselect archive' : 'Select archive'}
      >
        {selected ? '✓' : ''}
      </button>

      <Bubble
        useCase='date'
        content={
          startedDate
            ? `${monthName.getMonthName(
                startedDate.getMonth(),
                'shorthand'
              )} ${startedDate.getDate()}`
            : '--'
        }
      />

      {typeof streamInfo.profit === 'number' && (
        <Bubble useCase='profit' content={`$${streamInfo.profit}`} />
      )}

      <Bubble
        useCase='duration'
        content={formatDuration(streamInfo.durationMs)}
      />
    </div>
  )
}

interface BubbleProps {
  useCase: 'date' | 'duration' | 'profit'
  content: string
}

const Bubble = ({ useCase = 'date', content }: BubbleProps) => {
  return (
    <div className={`${styles.bubble} ${styles[useCase]}`}>
      {content}
    </div>
  )
}